// ============================================
// PRTCL (formerly PenRx) Router
// ============================================

import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useNotificationActions } from '../utils/useNotificationActions';
import { Layout } from './Layout';
import { AuthProvider, useAuth } from '../auth/AuthProvider';
import { AuthGuard } from '../auth/AuthGuard';
import { RequireNativeSurface, RedirectAuthOnWeb } from '../features/marketing/funnel/webLockdown';
import { FormPersistProvider } from '../data/FormPersistContext';
import { ErrorBoundary } from './ErrorBoundary';
import { ToastProvider } from '../components/Toast';
import { isNative } from '../utils/native';
import '../components/Toast.css';

// Retry wrapper for lazy imports - handles stale chunks after deploys
function lazyRetry(importFn: () => Promise<{ default: React.ComponentType<any> }>) {
  return lazy(() =>
    importFn().catch(() => {
      // Chunk failed to load (likely stale after deploy) - reload once
      const hasReloaded = sessionStorage.getItem('prtcl_chunk_reload');
      if (!hasReloaded) {
        sessionStorage.setItem('prtcl_chunk_reload', '1');
        window.location.reload();
        return { default: () => null } as any;
      }
      sessionStorage.removeItem('prtcl_chunk_reload');
      throw new Error('Failed to load page. Please refresh.');
    })
  );
}

// Auth pages
const SignInPage = lazyRetry(() => import('../auth/SignInPage'));
const SignUpPage = lazyRetry(() => import('../auth/SignUpPage'));
const ForgotPasswordPage = lazyRetry(() => import('../auth/ForgotPasswordPage'));
const ResetPasswordPage = lazyRetry(() => import('../auth/ResetPasswordPage'));
const DisclaimerPage = lazyRetry(() => import('../auth/DisclaimerPage'));
const SubscriptionPage = lazyRetry(() => import('../auth/SubscriptionPage'));

// Lazy load feature pages for code splitting
const LandingPage = lazyRetry(() => import('../features/marketing/LandingPage'));
const FirstPeptidePage = lazyRetry(() => import('../features/marketing/funnel/FirstPeptidePage'));
const ReconstitutionPage = lazyRetry(() => import('../features/marketing/funnel/ReconstitutionPage'));
const ReconCalculatorPage = lazyRetry(() => import('../features/marketing/funnel/ReconCalculatorPage'));
const TermsPage = lazyRetry(() => import('../features/marketing/TermsPage'));
const PrivacyPage = lazyRetry(() => import('../features/marketing/PrivacyPage'));
const ConsumerHealthDataPage = lazyRetry(() => import('../features/marketing/ConsumerHealthDataPage'));
const PublicFaqSharePage = lazyRetry(() => import('../features/marketing/PublicFaqSharePage'));
const HomePage = lazyRetry(() => import('../features/home/HomePage'));
const HistoryPage = lazyRetry(() => import('../features/history/HistoryPage'));
const ProtocolsPage = lazyRetry(() => import('../features/protocols/ProtocolsPage'));
// v3 cutover (spec E5): /protocols/new hosts the conversational wizard.
// The legacy AddProtocolPage is unrouted; it is deleted in Phase C after
// the entry doors are rewired.
const AddProtocolWizard = lazyRetry(() => import('../features/protocols/wizard/AddProtocolWizard'));
const EditProtocolPage = lazyRetry(() => import('../features/protocols/EditProtocolPage'));
const ProtocolDetailPage = lazyRetry(() => import('../features/protocols/ProtocolDetailPage'));
const PhaseConfirmationGate = lazyRetry(() => import('../features/protocols/PhaseConfirmationGate'));
const CalculatorPage = lazyRetry(() => import('../features/calculator/CalculatorPage'));
const LibraryPage = lazyRetry(() => import('../features/library/LibraryPage'));
const DetailPage = lazyRetry(() => import('../features/library/DetailPage'));
const AnalyzePage = lazyRetry(() => import('../features/library/AnalyzePage'));
const ReviewExtractedProtocol = lazyRetry(() => import('../features/video-analyzer/ReviewExtractedProtocol'));
const ProfilePage = lazyRetry(() => import('../features/profile/ProfilePage'));
const AddVialPage = lazyRetry(() => import('../features/profile/AddVialPage'));
const RefillVialPage = lazyRetry(() => import('../features/profile/RefillVialPage'));
const InjectionSiteMap = lazyRetry(() => import('../features/profile/InjectionSiteMap'));
const ResourcesPage = lazyRetry(() => import('../features/resources/ResourcesPage'));
const FaqDetailPage = lazyRetry(() => import('../features/resources/FaqDetailPage'));
const GuideDetailPage = lazyRetry(() => import('../features/resources/GuideDetailPage'));
const Peptides101 = lazyRetry(() => import('../features/learn/Peptides101'));
const ReferencesPage = lazyRetry(() => import('../features/references/ReferencesPage'));
const OnboardingFlow = lazyRetry(() => import('../features/onboarding/OnboardingFlow'));
const WalkthroughV2 = lazyRetry(() => import('../features/calculator/WalkthroughV2'));
const BetaOnboarding = lazyRetry(() => import('../features/beta-onboarding/BetaOnboarding'));
const WelcomeFlow = lazyRetry(() => import('../features/welcome/WelcomeFlow'));
const NotificationDebugPage = lazyRetry(() => import('../features/debug/NotificationDebugPage'));

const PageLoader: React.FC = () => (
  <div className="page">
    <div className="dash-loading" style={{ paddingTop: 'var(--space-8)' }}>
      <div className="dash-loading-pulse" />
      <div className="dash-loading-pulse" />
      <div className="dash-loading-pulse" />
    </div>
  </div>
);

const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

// Bump this number whenever you want every existing user to re-see the
// Welcome Flow on next cold launch (e.g. major redesign of the intro,
// new "what's new" content). The version is stored in localStorage and
// the RootRoute redirects to /welcome whenever the persisted version is
// below this constant. Set to 1 on 2026-05-12 — the first version with
// the 5-slide post-auth-too intro.
export const WELCOME_FLOW_VERSION = 1;

// Read what version of the welcome flow this device has already seen.
// Supports the legacy boolean flag (prtcl_welcome_flow_seen=true) — if
// present, it's treated as having seen version 1.
//
// Exported for unit testing — also accepts an optional storage shim so
// tests can pass a mock Storage instead of touching real localStorage.
export function getSeenWelcomeVersion(storage: Pick<Storage, 'getItem'> = localStorage): number {
  try {
    const versionStr = storage.getItem('prtcl_welcome_flow_version');
    if (versionStr !== null) {
      const n = parseInt(versionStr, 10);
      return Number.isFinite(n) ? n : 0;
    }
    if (storage.getItem('prtcl_welcome_flow_seen') === 'true') return 1;
    return 0;
  } catch { return 0; }
}

// Root route: renders LandingPage on web (marketing for prtclapp.com).
// On native gate chain:
//   - welcome flow not seen at current version  -> /welcome
//     (fires for new AND authenticated existing users; the flow itself
//      adapts its path-picker CTAs based on auth state)
//   - authed                                    -> /dashboard
//   - not authed: see the signed-out table below
const RootRoute: React.FC = () => {
  const { session, loading } = useAuth();
  // Web homepage = the tour experience (promoted from /v2 on 2026-07-23).
  if (!isNative()) return <LandingPage tour />;
  if (loading) return <PageLoader />;
  if (getSeenWelcomeVersion() < WELCOME_FLOW_VERSION) {
    return <Navigate to="/welcome" replace />;
  }
  if (session) return <Navigate to="/dashboard" replace />;
  // Signed-out cold-launch table (route-audit F1/F2, 2026-07-18). The
  // old logic keyed everything on prtcl_welcome_seen_v2, which was
  // written on ANY BetaOnboarding unmount - so a brand-new user who
  // merely peeked at the walkthrough relaunched onto the sign-in screen
  // ("Welcome back" for someone with no account). Decision order now:
  //   1. A walkthrough-built protocol is stashed -> /signup (finishing
  //      signup is what saves it; endowment framing intact).
  //   2. Walkthrough reached an endpoint (finale/skip/signup - the _v2
  //      flag is now written at markFlowCompleted, not on unmount)
  //      -> /signin (they may already have created the account).
  //   3. They picked an account path on the welcome picker but never
  //      entered the walkthrough -> honor it (/signup or /signin).
  //   4. Otherwise -> resume /beta-onboarding.
  const flag = (k: string) => {
    try { return localStorage.getItem(k); } catch { return null; }
  };
  if (flag('prtcl-pending-beta-protocol')) return <Navigate to="/signup" replace />;
  if (flag('prtcl_welcome_seen_v2') === 'true') return <Navigate to="/signin" replace />;
  const welcomePath = flag('prtcl_welcome_path');
  if (welcomePath === 'signup') return <Navigate to="/signup" replace />;
  if (welcomePath === 'signin') return <Navigate to="/signin" replace />;
  return <Navigate to="/beta-onboarding" replace />;
};

// Tiny wrapper so the notification action hook can mount inside the
// router (useNavigate requires a Router ancestor). Renders nothing.
const NotificationActionListener: React.FC = () => {
  useNotificationActions();
  return null;
};

const NotFoundPage: React.FC = () => {
  const navigate = useNavigate();
  return (
    <div className="page" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: '1rem' }}>
      <h1 style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700 }}>Page not found</h1>
      <p style={{ color: 'var(--color-text-secondary)' }}>The page you're looking for doesn't exist.</p>
      <button
        onClick={() => navigate('/')}
        style={{ padding: '0.75rem 1.5rem', borderRadius: '8px', border: 'none', background: 'var(--color-accent)', color: '#000', fontWeight: 600, cursor: 'pointer' }}
      >
        Go Home
      </button>
    </div>
  );
};

export const Router: React.FC = () => (
  <ToastProvider>
  <BrowserRouter>
    <ScrollToTop />
    {/* ErrorBoundary lives inside BrowserRouter so its fallback can call
        useNavigate() for SPA recovery (no full page reload). The trade-off:
        an error inside BrowserRouter or ScrollToTop itself won't be caught
        — those are stable enough that we accept it. */}
    <ErrorBoundary>
    <AuthProvider>
      <FormPersistProvider>
        <NotificationActionListener />
        <Layout>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public auth routes. WEB LOCKDOWN (marketingConfig switch):
                  sign in / sign up / forgot-password redirect to the landing
                  page on web; /reset-password stays FUNCTIONAL on web so iOS
                  users can complete a password reset in the browser; the
                  post-auth gate pages show the App Store notice on web. */}
              <Route path="/signin" element={<RedirectAuthOnWeb><SignInPage /></RedirectAuthOnWeb>} />
              <Route path="/signup" element={<RedirectAuthOnWeb><SignUpPage /></RedirectAuthOnWeb>} />
              <Route path="/forgot-password" element={<RedirectAuthOnWeb><ForgotPasswordPage /></RedirectAuthOnWeb>} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/disclaimer" element={<RequireNativeSurface><DisclaimerPage /></RequireNativeSurface>} />
              <Route path="/subscribe" element={<RequireNativeSurface><SubscriptionPage /></RequireNativeSurface>} />

              {/* Public marketing / guide surfaces - available on web and native */}
              <Route path="/" element={<RootRoute />} />
              {/* /v2 was the tour preview; the tour is now the homepage, so
                  /v2 redirects home (also 301'd at the edge in vercel.json). */}
              <Route path="/v2" element={<Navigate to="/" replace />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/consumer-health-data" element={<ConsumerHealthDataPage />} />
              <Route path="/share/faq/:slug" element={<PublicFaqSharePage />} />
              <Route path="/learn" element={<Peptides101 />} />
              {/* App Review 1.4.1: the app-wide source index must be reachable
                  without an account, the same as /learn, so the citations are
                  easy for any user (or reviewer) to find. */}
              <Route path="/references" element={<ReferencesPage />} />
              <Route path="/your-first-peptide" element={<FirstPeptidePage />} />
              <Route path="/reconstitution" element={<ReconstitutionPage />} />
              <Route path="/tools/reconstitution-calculator" element={<ReconCalculatorPage />} />
              {/* /beta-onboarding stays a public demo on web; its signup step
                  is replaced with email capture inside BetaOnboarding. */}
              <Route path="/beta-onboarding" element={<BetaOnboarding />} />

              {/* App surfaces - native only. On web every one renders the App
                  Store notice (RequireNativeSurface), so there is no way into
                  the authenticated web app. AuthGuard still runs on native. */}
              <Route path="/welcome" element={<RequireNativeSurface><WelcomeFlow /></RequireNativeSurface>} />
              <Route path="/dashboard" element={<RequireNativeSurface><AuthGuard><HomePage /></AuthGuard></RequireNativeSurface>} />
              <Route path="/history" element={<RequireNativeSurface><AuthGuard><HistoryPage /></AuthGuard></RequireNativeSurface>} />
              <Route path="/protocols" element={<RequireNativeSurface><AuthGuard><ProtocolsPage /></AuthGuard></RequireNativeSurface>} />
              <Route path="/protocols/new" element={<RequireNativeSurface><AuthGuard><AddProtocolWizard /></AuthGuard></RequireNativeSurface>} />
              <Route path="/protocols/:id" element={<RequireNativeSurface><AuthGuard><ProtocolDetailPage /></AuthGuard></RequireNativeSurface>} />
              <Route path="/protocols/:id/edit" element={<RequireNativeSurface><AuthGuard><EditProtocolPage /></AuthGuard></RequireNativeSurface>} />
              <Route path="/protocols/:id/phase-gate" element={<RequireNativeSurface><AuthGuard><PhaseConfirmationGate /></AuthGuard></RequireNativeSurface>} />
              <Route path="/calculator" element={<RequireNativeSurface><AuthGuard><CalculatorPage /></AuthGuard></RequireNativeSurface>} />
              <Route path="/calculator/walkthrough" element={<RequireNativeSurface><AuthGuard><WalkthroughV2 /></AuthGuard></RequireNativeSurface>} />
              <Route path="/library" element={<RequireNativeSurface><LibraryPage /></RequireNativeSurface>} />
              <Route path="/library/analyze" element={<RequireNativeSurface><AuthGuard><AnalyzePage /></AuthGuard></RequireNativeSurface>} />
              <Route path="/video/:videoId/review" element={<RequireNativeSurface><AuthGuard><ReviewExtractedProtocol /></AuthGuard></RequireNativeSurface>} />
              <Route path="/library/:slug" element={<RequireNativeSurface><DetailPage recordKind="peptide" /></RequireNativeSurface>} />
              <Route path="/library/stack/:slug" element={<RequireNativeSurface><DetailPage recordKind="stack" /></RequireNativeSurface>} />
              <Route path="/profile" element={<RequireNativeSurface><AuthGuard><ProfilePage /></AuthGuard></RequireNativeSurface>} />
              {/* My Supply unification (2026-07-14): the standalone Inventory
                  page is gone - My Supply is now a tab on /protocols. This
                  redirect keeps every old bookmark/deep-link working. */}
              <Route path="/profile/inventory" element={<Navigate to="/protocols?tab=supply" replace />} />
              <Route path="/profile/inventory/add" element={<RequireNativeSurface><AuthGuard><AddVialPage /></AuthGuard></RequireNativeSurface>} />
              <Route path="/profile/inventory/refill/:vialId" element={<RequireNativeSurface><AuthGuard><RefillVialPage /></AuthGuard></RequireNativeSurface>} />
              <Route path="/profile/injection-sites" element={<RequireNativeSurface><AuthGuard><InjectionSiteMap /></AuthGuard></RequireNativeSurface>} />
              <Route path="/resources" element={<RequireNativeSurface><AuthGuard><ResourcesPage /></AuthGuard></RequireNativeSurface>} />
              <Route path="/resources/guides/:slug" element={<RequireNativeSurface><AuthGuard><GuideDetailPage /></AuthGuard></RequireNativeSurface>} />
              <Route path="/resources/:slug" element={<RequireNativeSurface><AuthGuard><FaqDetailPage /></AuthGuard></RequireNativeSurface>} />
              <Route path="/onboarding" element={<RequireNativeSurface><AuthGuard><OnboardingFlow /></AuthGuard></RequireNativeSurface>} />
              <Route path="/debug/notifications" element={<RequireNativeSurface><AuthGuard><NotificationDebugPage /></AuthGuard></RequireNativeSurface>} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </Layout>
      </FormPersistProvider>
    </AuthProvider>
    </ErrorBoundary>
  </BrowserRouter>
  </ToastProvider>
);
